import React, { useState } from "react";
import { DatePicker, Input, Modal } from "antd";
import dayjs, { Dayjs } from "dayjs";

import { errMsg } from "../../helpers/errMsg.helper";

interface CreateSprintInlineProps {
  open: boolean;
  onSubmit: (payload: {
    name: string;
    goal: string | null;
    start_date: string | null;
    end_date: string | null;
  }) => Promise<void>;
  onCancel: () => void;
  submitting?: boolean;
}

const CreateSprintInline: React.FC<CreateSprintInlineProps> = ({
  open,
  onSubmit,
  onCancel,
  submitting = false,
}) => {
  const [name, setName] = useState("");
  const [goal, setGoal] = useState("");
  const [start, setStart] = useState<Dayjs | null>(null);
  const [end, setEnd] = useState<Dayjs | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName("");
    setGoal("");
    setStart(null);
    setEnd(null);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError("Sprint name is required");
      return;
    }
    if (start && end && end.isBefore(start, "day")) {
      setError("End date must be after start date");
      return;
    }
    try {
      await onSubmit({
        name: name.trim(),
        goal: goal.trim() || null,
        start_date: start ? start.format("YYYY-MM-DD") : null,
        end_date: end ? end.format("YYYY-MM-DD") : null,
      });
      reset();
    } catch (e: any) {
      setError(errMsg(e, "Failed to create sprint"));
    }
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      title="New Sprint"
      destroyOnClose
      maskClosable={false}
      keyboard={false}
      centered
      width="640px"
      zIndex={1001}
      styles={{
        content: {
          padding: 0,
          borderRadius: 12,
          overflow: "hidden",
        },
        header: {
          padding: "14px 20px 12px",
          borderBottom: "1px solid #f0f0f0",
          marginBottom: 0,
        },
        body: {
          padding: "16px 24px 20px",
        },
        footer: {
          padding: "10px 20px 14px",
          borderTop: "1px solid #f0f0f0",
          marginTop: 0,
        },
        mask: {
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          background: "rgba(0,0,0,0.45)",
        },
      }}
      footer={
        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-light"
            onClick={handleCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-info"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Creating…" : "Create Sprint"}
          </button>
        </div>
      }
    >
      <div className="d-flex flex-column gap-3">
        {error && (
          <div className="alert alert-danger py-2 mb-0">{error}</div>
        )}
        <div>
          <label className="form-label small fw-semibold">
            Name <span className="text-danger">*</span>
          </label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sprint 12"
          />
        </div>
        <div>
          <label className="form-label small fw-semibold">Goal</label>
          <Input.TextArea
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="What is this sprint trying to accomplish?"
            rows={3}
          />
        </div>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label small fw-semibold">Start Date</label>
            <DatePicker
              style={{ width: "100%" }}
              value={start}
              onChange={(d) => setStart(d)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-semibold">End Date</label>
            <DatePicker
              style={{ width: "100%" }}
              value={end}
              onChange={(d) => setEnd(d)}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreateSprintInline;
